import { useState, useRef, useCallback, useEffect } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Send, Paperclip } from 'lucide-react'
import { CHAT_FILE_MAX_SIZE, CHAT_FILE_ALLOWED_TYPES } from '@/core/types/chat'

interface ChatInputProps {
  onSend: (content: string) => void
  disabled?: boolean
  placeholder?: string
  onTyping?: (isTyping: boolean) => void
  onAttachment?: (file: File) => void
}

export function ChatInput({ onSend, disabled = false, placeholder = 'Escribí un mensaje...', onTyping, onAttachment }: ChatInputProps) {
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const typingRef = useRef(false)
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const emitTyping = useCallback((isTyping: boolean) => {
    if (!onTyping) return
    if (isTyping === typingRef.current) return
    typingRef.current = isTyping
    onTyping(isTyping)
  }, [onTyping])

  const handleSend = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed || disabled) return
    onSend(trimmed)
    setValue('')
    emitTyping(false)
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    // Auto-resize back to min height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [value, disabled, onSend, emitTyping])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
    // Auto-resize textarea
    const el = e.target
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`

    // Typing indicator
    emitTyping(true)
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    typingTimeoutRef.current = setTimeout(() => emitTyping(false), 3000)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !onAttachment) return

    // H5: feedback explícito al usuario cuando el archivo no es válido.
    // Antes retornaba silenciosamente causando confusión.
    if (file.size > CHAT_FILE_MAX_SIZE) {
      toast.error('El archivo excede 10MB')
      e.target.value = ''
      return
    }
    if (file.type && !CHAT_FILE_ALLOWED_TYPES.includes(file.type)) {
      toast.error('Tipo de archivo no permitido')
      e.target.value = ''
      return
    }

    onAttachment(file)
    e.target.value = '' // Reset for re-selection
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    }
  }, [])

  return (
    <div className="flex items-end gap-1.5 p-3 border-t bg-background">
      {onAttachment && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 h-9 w-9"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            title="Adjuntar archivo"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={CHAT_FILE_ALLOWED_TYPES.join(',')}
            onChange={handleFileChange}
          />
        </>
      )}
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        className="min-h-[36px] max-h-[120px] resize-none text-sm"
      />
      <Button
        size="icon"
        onClick={handleSend}
        disabled={disabled || !value.trim()}
        className="shrink-0 h-9 w-9"
      >
        <Send className="h-4 w-4" />
      </Button>
    </div>
  )
}
